﻿import {
  Checkbox,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import {
  BasicModal,
  LTLProductCatalog,
  Popover,
  SelectInput,
  useIndexedPopoverState,
} from '@components';
import { ToastType } from '@components/Toast';
import { LTLLoadItem, LTLQuoteLoadItemBreakdown } from '@store/ltl';
import { closeModal, openModal } from '@store/modals';
import { initialHazmatDetails } from '@store/orders/defaultVals';
import { PlusIcon, DeleteIcon, PencilIcon } from '@atgfr8/icons';
import { LTLCatalogs } from '@typeDefinitions/ltlTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { HazardousMaterial } from '@views/order/components/carrier/stops/stopsEditor/HazardousMaterial';
import { Component, Index, createSignal, createUniqueId } from 'solid-js';

import { LTlFormClasses as cls } from '../classes';
import { freightClasses } from '../constants';
import { LTlFormStyles } from '../styles';

const cellStyles = {
  'padding-block': '10px',
  'padding-inline': '10px',
  'border-bottom': '0px',
  color: 'black',
};

interface QuoteLoadItemBreakdownProps {
  rowData: () => LTLLoadItem;
  data: () => {
    loadItems: LTLLoadItem[];
  };
  setFields: (path: string, value: unknown) => void;
  handleOnChangeForLinearfeet: (index: number) => Promise<void>;
  getCalculationsFromLoadItems: () => Promise<void>;
  errors: (path: string) => boolean;
  setLoadItemDimChange: (value: boolean) => void;
  loadItemIndex: number;
  customerId: number | null;
}

export const QuoteLoadItemBreakdown: Component<QuoteLoadItemBreakdownProps> = (
  props,
) => {
  // Scoped per instance: without it every load item's sub-item N shared the
  // same modal id (and so did any second mounted quote form).
  const instanceId = createUniqueId();
  const catalogModalId = (index: number) =>
    `sidebar-ltl-product-catalog-${instanceId}-${index}`;
  const [cachedHazmatDetails, setCachedHazmatDetails] = createSignal<
    Record<number, unknown>
  >({});
  const hazmatPopover = useIndexedPopoverState(
    (index) => `[data-hazmat-row-key="${props.loadItemIndex}-${index}"]`,
  );

  const remapHazmatCacheAfterDelete = (removedIndex: number) => {
    setCachedHazmatDetails((previousCache) => {
      const nextCache: Record<number, unknown> = {};

      for (const [indexKey, hazmatDetail] of Object.entries(previousCache)) {
        const currentIndex = Number(indexKey);
        if (Number.isNaN(currentIndex) || currentIndex === removedIndex) {
          continue;
        }

        const targetIndex =
          currentIndex > removedIndex ? currentIndex - 1 : currentIndex;
        nextCache[targetIndex] = hazmatDetail;
      }

      return nextCache;
    });
  };

  const handleHazmatChange = (
    rowData: () => LTLQuoteLoadItemBreakdown,
    loadItemIndex: number,
    index: number,
  ) => {
    const isHazmat = Boolean(rowData().hazmat);
    const hazmatPath = `loadItems.${loadItemIndex}.quoteLoadItemBreakdown.${index}.hazmat`;
    const hazmatDetailPath = `loadItems.${loadItemIndex}.quoteLoadItemBreakdown.${index}.loadItemHazmatDetail`;

    if (isHazmat) {
      const currentHazmatDetail =
        props.data().loadItems[loadItemIndex]?.quoteLoadItemBreakdown?.[index]
          ?.loadItemHazmatDetail;

      if (currentHazmatDetail) {
        setCachedHazmatDetails((prev) => ({
          ...prev,
          [index]: currentHazmatDetail,
        }));
      }

      props.setFields(hazmatPath, false);
      props.setFields(hazmatDetailPath, null);
      hazmatPopover.close();
      return;
    }

    props.setFields(hazmatPath, true);
    const restoredHazmatDetail = cachedHazmatDetails()[index];
    props.setFields(
      hazmatDetailPath,
      restoredHazmatDetail ?? initialHazmatDetails,
    );

    if (restoredHazmatDetail) {
      handleToast(ToastType.Info, 'Previous hazmat details restored.');
    }

    hazmatPopover.open(index);
  };

  const hasHazmatValues = (detail: unknown) => {
    if (!detail || typeof detail !== 'object') return false;

    const hazmatDetail = detail as Partial<{
      unNumber: string | null;
      packingGroup: string | null;
      hazardClass: string | null;
      piece: string | null;
      contactNumber: string | null;
      contactName: string | null;
    }>;

    return [
      hazmatDetail.unNumber,
      hazmatDetail.packingGroup,
      hazmatDetail.hazardClass,
      hazmatDetail.piece,
      hazmatDetail.contactNumber,
      hazmatDetail.contactName,
    ].some((value) => Boolean(value?.trim()));
  };

  return (
    <Index
      each={props.data().loadItems[props.loadItemIndex].quoteLoadItemBreakdown}
    >
      {(rowData, index) => (
        <>
          <TableRow>
            <TableCell style={cellStyles}></TableCell>
            <TableCell style={cellStyles}></TableCell>
            <TableCell style={cellStyles}>
              <TextField
                label=""
                class="h-[36px]! [&>input]:text-black!"
                size="sm"
                placeholder="Description"
                value={rowData().description}
                onChange={(e: string) =>
                  props.setFields(
                    `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.description`,
                    e,
                  )
                }
                endIcon={
                  props.customerId === null ? null : (
                    <IconButton
                      dataTestId={`quote-load-item-breakdown-add-product-button-${index}`}
                      onClick={() => openModal(catalogModalId(index))}
                      size="sm"
                    >
                      <PlusIcon />
                    </IconButton>
                  )
                }
                dataTestId={'description-textInput'}
              />
            </TableCell>
            <TableCell style={cellStyles}>
              <TextField
                label=""
                type="number"
                placeholder="#"
                class="h-[36px]! [&>input]:text-black!"
                size="sm"
                maxLength={8}
                value={rowData().pieces ?? ''}
                onChange={(e: unknown) =>
                  props.setFields(
                    `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.pieces`,
                    e as number,
                  )
                }
                dataTestId={'pieces-textInput'}
              />
            </TableCell>
            <TableCell style={cellStyles}>
              <TextField
                label=""
                type="number"
                class="h-[36px]! [&>input]:text-black!"
                size="sm"
                maxLength={6}
                value={rowData().weight ?? ''}
                dataTestId="weight"
                onChange={(e: unknown) => {
                  if (
                    e !==
                    props.data().loadItems[props.loadItemIndex]
                      ?.quoteLoadItemBreakdown?.[index]?.weight
                  ) {
                    props.setLoadItemDimChange(true);
                  }
                  props.setFields(
                    `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.weight`,
                    e as number,
                  );
                  void props.handleOnChangeForLinearfeet(props.loadItemIndex);
                }}
              />
            </TableCell>
            <TableCell style={cellStyles}>
              <Stack direction="row" spacing="5px">
                <></>
              </Stack>
            </TableCell>
            <TableCell style={cellStyles}>
              <TextField
                label=""
                maxLength={10}
                class="h-[36px]! [&>input]:text-black!"
                size="sm"
                placeholder="NMFC"
                value={rowData().nmfc ?? ''}
                onChange={(e: string) =>
                  props.setFields(
                    `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.nmfc`,
                    e,
                  )
                }
                dataTestId="dataTestId"
              />
            </TableCell>
            <TableCell style={cellStyles}>
              <SelectInput
                label=""
                class="h-[36px]!"
                style={{ width: '100%' }}
                value={rowData().class?.toString() ?? ''}
                onChange={(val) => {
                  props.setFields(
                    `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.class`,
                    val,
                  );
                }}
                menuItems={freightClasses.map((fc) => ({
                  label: fc,
                  value: fc,
                }))}
                dataTestId={formatDataTestId(
                  'Quote freight class',
                  'selectInput',
                )}
              />
            </TableCell>
            <TableCell style={cellStyles}>
              <div
                class={cls.hazmatPopOverContainer}
                data-hazmat-row-key={`${props.loadItemIndex}-${index}`}
              >
                <Checkbox
                  size="xs"
                  color="info"
                  label=""
                  style={LTlFormStyles.hazmatCheckBox}
                  checked={Boolean(rowData().hazmat)}
                  onChange={() =>
                    handleHazmatChange(rowData, props.loadItemIndex, index)
                  }
                  dataTestId={'hazmat-checkboxInput'}
                />
                <Popover
                  forceClosePopover={!rowData().hazmat}
                  triggerClose={hazmatPopover.triggerClose(index)}
                  onClick={hazmatPopover.onTriggerClick(index)}
                  preventFormSubmit
                  eleName={
                    <PencilIcon
                      class={
                        rowData().hazmat ? 'text-[#026EA1]' : 'text-gray-500'
                      }
                    />
                  }
                  classes={cls.hazmatPopOver}
                  sxProps={LTlFormStyles.hazmatPopOver}
                >
                  <HazardousMaterial
                    fromSidebar={true}
                    loadItemHazmatDetail={
                      props.data().loadItems[props.loadItemIndex]
                        ?.quoteLoadItemBreakdown?.[index]
                        ?.loadItemHazmatDetail ?? initialHazmatDetails
                    }
                    onSave={(hazmatDetail) => {
                      setCachedHazmatDetails((prev) => ({
                        ...prev,
                        [index]: hazmatDetail,
                      }));

                      props.setFields(
                        `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.loadItemHazmatDetail`,
                        hazmatDetail,
                      );

                      props.setFields(
                        `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.hazmat`,
                        true,
                      );

                      hazmatPopover.close();
                    }}
                    onClose={() => {
                      const currentHazmatDetail =
                        props.data().loadItems[props.loadItemIndex]
                          ?.quoteLoadItemBreakdown?.[index]
                          ?.loadItemHazmatDetail;

                      if (!hasHazmatValues(currentHazmatDetail)) {
                        props.setFields(
                          `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.hazmat`,
                          false,
                        );
                        props.setFields(
                          `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}.loadItemHazmatDetail`,
                          null,
                        );
                      }

                      hazmatPopover.close();
                    }}
                  />
                </Popover>
              </div>
            </TableCell>
            <TableCell style={cellStyles}></TableCell>
            <TableCell style={cellStyles}></TableCell>
            <TableCell style={cellStyles}>
              <div class="flex justify-end">
                <IconButton
                  dataTestId={`quote-load-item-breakdown-delete-button-${index}`}
                  onClick={() => {
                    remapHazmatCacheAfterDelete(index);
                    const filteredLoadItems = props
                      .data()
                      .loadItems[
                        props.loadItemIndex
                      ]?.quoteLoadItemBreakdown?.filter((_, i) => i !== index);
                    props.setFields(
                      `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown`,
                      filteredLoadItems,
                    );
                  }}
                >
                  <DeleteIcon class={cls.deleteIcon} />
                </IconButton>
              </div>
            </TableCell>
          </TableRow>
          <BasicModal
            id={catalogModalId(index)}
            footer={false}
            width="1300px"
            maxWidth="95vw"
            title=" Select LTL Product Catalog"
          >
            <LTLProductCatalog
              customerId={props.customerId!}
              goBackButtonText="< Back to LTL Quoting"
              goBack={() => closeModal(catalogModalId(index))}
              onRowClick={(rowData: LTLCatalogs) => {
                const newValue = {
                  class: rowData.class,
                  description: rowData.name,
                  hazmat: rowData.hazmat,
                  nmfc: rowData.nmfc,
                  weight: rowData.weight,
                  loadItemHazmatDetail: {
                    unNumber: rowData.unNumber,
                    piece: rowData.piece,
                    packingGroup: rowData.packingGroup,
                    hazardClass: rowData.hazardClass,
                    contactNumber: rowData.contactNumber,
                    contactName: rowData.contactName,
                  },
                };
                props.setFields(
                  `loadItems.${props.loadItemIndex}.quoteLoadItemBreakdown.${index}`,
                  {
                    ...props.data().loadItems[props.loadItemIndex]
                      ?.quoteLoadItemBreakdown?.[index],
                    ...newValue,
                  },
                );

                closeModal(catalogModalId(index));
              }}
              closeModal={closeModal}
              openModal={openModal}
            />
          </BasicModal>
        </>
      )}
    </Index>
  );
};
